// LLM generated - Model Claude 4 Sonnet
/**
 * useLiveActivityChat - Pusher chat subscription hook
 *
 * Features:
 * 1. Passes full payload data to callbacks (not just chatMode)
 * 2. Connection fallback callbacks for handling disconnects
 * 3. Timeout mechanism for expected events (10 second fallback)
 * 4. Uses typed payloads from pusher-events.ts
 */
import { useContext, useEffect, useRef, useCallback } from 'react';

import { logError } from '~/common/utils/log';
import { PusherContext } from '~/domains/pusher/PusherContext';
import type { Chat, ChatChannel } from '../types';
import type {
  ChatMode,
  PusherMessageCreatedPayload,
  PusherMessageUpdatedPayload,
  PusherMessageDeletedPayload,
  PusherReactionAddedPayload,
  PusherReactionRemovedPayload,
  PusherChatStatePayload,
  PusherUserBannedPayload,
} from '../types/pusher-events';
import { PUSHER_TIMEOUT } from '../types/pusher-events';

// =============================================================================
// TYPES
// =============================================================================

type UseLiveActivityChatOptions = {
  /** Array of chat objects to subscribe to */
  chats?: Chat[];
  /** Whether Pusher subscriptions are enabled */
  enabled?: boolean;

  // Message event callbacks
  onMessageCreated?: (chatMode: ChatMode, message: PusherMessageCreatedPayload) => void;
  onMessageUpdated?: (chatMode: ChatMode, update: PusherMessageUpdatedPayload) => void;
  onMessageDeleted?: (chatMode: ChatMode, payload: PusherMessageDeletedPayload) => void;

  // Reaction event callbacks
  onReactionAdded?: (chatMode: ChatMode, payload: PusherReactionAddedPayload) => void;
  onReactionRemoved?: (chatMode: ChatMode, payload: PusherReactionRemovedPayload) => void;

  // Chat state event callbacks
  onChatStateChanged?: (chatMode: ChatMode, payload: PusherChatStatePayload) => void;
  onUserBanned?: (chatMode: ChatMode, payload: PusherUserBannedPayload) => void;

  // Connection fallback callbacks
  onConnectionLost?: () => void;
  onConnectionRestored?: () => void;
  onSubscriptionError?: (error: Error) => void;
  onEventTimeout?: (eventType: string, chatMode: ChatMode) => void;
};

type UseLiveActivityChatReturn = {
  /** Whether the Pusher connection is active */
  isConnected: boolean;
  /** Whether channel subscriptions are active */
  isSubscribed: boolean;
  /** Whether there's a Pusher error */
  hasError: boolean;
  /** The error object if any */
  error: Error | null;
  /**
   * Start a timeout for an expected event.
   * Call this when you perform an action that should trigger a Pusher event.
   * If the event doesn't arrive within PUSHER_TIMEOUT, onEventTimeout is called.
   */
  expectEvent: (eventType: string, chatMode: ChatMode) => void;
  /**
   * Cancel a pending timeout for an expected event.
   * Call this when the expected event arrives.
   */
  cancelExpectedEvent: (eventType: string, chatMode: ChatMode) => void;
};

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

const isPresenceChannel = (channel: ChatChannel): boolean =>
  channel?.type === 'presence' || Boolean(channel?.name?.startsWith('presence-'));

export const useLiveActivityChat = ({
  chats = [],
  enabled = true,
  // Message callbacks
  onMessageCreated,
  onMessageUpdated,
  onMessageDeleted,
  // Reaction callbacks
  onReactionAdded,
  onReactionRemoved,
  // Chat state callbacks
  onChatStateChanged,
  onUserBanned,
  // Connection callbacks
  onConnectionLost,
  onConnectionRestored,
  onSubscriptionError,
  onEventTimeout,
}: UseLiveActivityChatOptions): UseLiveActivityChatReturn => {
  // Track subscription state
  const isSubscribedRef = useRef(false);

  // Track previous connection state for detecting changes
  const wasConnectedRef = useRef(false);

  // Track expected events and their timeouts
  const expectedEventsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Get Pusher context
  const { subscribeToChannel, unsubscribeFromChannel, isConnected, hasError, error } =
    useContext(PusherContext);

  // ---------------------------------------------------------------------------
  // Event Timeout Management
  // ---------------------------------------------------------------------------

  const getEventKey = (eventType: string, chatMode: ChatMode) => `${eventType}:${chatMode}`;

  const expectEvent = useCallback(
    (eventType: string, chatMode: ChatMode) => {
      const key = getEventKey(eventType, chatMode);

      // Clear any existing timeout for this event
      const existingTimeout = expectedEventsRef.current.get(key);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set new timeout
      const timeoutId = setTimeout(() => {
        expectedEventsRef.current.delete(key);
        onEventTimeout?.(eventType, chatMode);
      }, PUSHER_TIMEOUT);

      expectedEventsRef.current.set(key, timeoutId);
    },
    [onEventTimeout]
  );

  const cancelExpectedEvent = useCallback((eventType: string, chatMode: ChatMode) => {
    const key = getEventKey(eventType, chatMode);
    const timeoutId = expectedEventsRef.current.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      expectedEventsRef.current.delete(key);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Connection State Monitoring
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Detect connection state changes
    if (!isConnected && wasConnectedRef.current) {
      // Connection was lost
      onConnectionLost?.();
    } else if (isConnected && !wasConnectedRef.current && wasConnectedRef.current !== undefined) {
      // Connection restored (but not on initial mount)
      onConnectionRestored?.();
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected, onConnectionLost, onConnectionRestored]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleMessageCreated = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const message = data as PusherMessageCreatedPayload;
      cancelExpectedEvent('message-created', chatMode);
      onMessageCreated?.(chatMode, message);
    },
    [onMessageCreated, cancelExpectedEvent]
  );

  const handleMessageUpdated = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const update = data as PusherMessageUpdatedPayload;
      cancelExpectedEvent('message-updated', chatMode);
      onMessageUpdated?.(chatMode, update);
    },
    [onMessageUpdated, cancelExpectedEvent]
  );

  const handleMessageDeleted = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const payload = data as PusherMessageDeletedPayload;
      cancelExpectedEvent('message-deleted', chatMode);
      onMessageDeleted?.(chatMode, payload);
    },
    [onMessageDeleted, cancelExpectedEvent]
  );

  const handleMessagePinned = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      // Pin events are a type of update
      // Note: Pusher payload may have 'messageId' instead of 'id', so we normalize it
      const rawPayload = data as Record<string, unknown>;
      const update: PusherMessageUpdatedPayload = {
        id: (rawPayload.id as string) || (rawPayload.messageId as string),
        isPinned: true,
        updatedAt: (rawPayload.updatedAt as string) || new Date().toISOString(),
        ...(rawPayload.payload && {
          payload: rawPayload.payload as PusherMessageUpdatedPayload['payload'],
        }),
      };
      cancelExpectedEvent('message-pinned', chatMode);
      onMessageUpdated?.(chatMode, update);
    },
    [onMessageUpdated, cancelExpectedEvent]
  );

  const handleMessageUnpinned = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      // Unpin events are a type of update
      // Note: Pusher payload may have 'messageId' instead of 'id', so we normalize it
      const rawPayload = data as Record<string, unknown>;
      const update: PusherMessageUpdatedPayload = {
        id: (rawPayload.id as string) || (rawPayload.messageId as string),
        isPinned: false,
        updatedAt: (rawPayload.updatedAt as string) || new Date().toISOString(),
        ...(rawPayload.payload && {
          payload: rawPayload.payload as PusherMessageUpdatedPayload['payload'],
        }),
      };
      cancelExpectedEvent('message-unpinned', chatMode);
      onMessageUpdated?.(chatMode, update);
    },
    [onMessageUpdated, cancelExpectedEvent]
  );

  const handleReactionAdded = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const payload = data as PusherReactionAddedPayload;
      cancelExpectedEvent('reaction-added', chatMode);
      onReactionAdded?.(chatMode, payload);
    },
    [onReactionAdded, cancelExpectedEvent]
  );

  const handleReactionRemoved = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const payload = data as PusherReactionRemovedPayload;
      cancelExpectedEvent('reaction-removed', chatMode);
      onReactionRemoved?.(chatMode, payload);
    },
    [onReactionRemoved, cancelExpectedEvent]
  );

  const handleChatEnabled = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const payload = data as PusherChatStatePayload;
      onChatStateChanged?.(chatMode, { ...payload, isEnabled: true });
    },
    [onChatStateChanged]
  );

  const handleChatDisabled = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const payload = data as PusherChatStatePayload;
      onChatStateChanged?.(chatMode, { ...payload, isEnabled: false });
    },
    [onChatStateChanged]
  );

  const handleChatDeactivated = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const payload = data as PusherChatStatePayload;
      onChatStateChanged?.(chatMode, { ...payload, isEnabled: false });
    },
    [onChatStateChanged]
  );

  const handleUserBanned = useCallback(
    (data: unknown, chatMode: ChatMode) => {
      const payload = data as PusherUserBannedPayload;
      onUserBanned?.(chatMode, payload);
    },
    [onUserBanned]
  );

  const handleSubscriptionSuccess = useCallback(() => {
    isSubscribedRef.current = true;
  }, []);

  const handleSubscriptionError = useCallback(
    (err: unknown) => {
      logError('useLiveActivityChat: Subscription error:', err);
      isSubscribedRef.current = false;
      const subscriptionError = err instanceof Error ? err : new Error(String(err));
      onSubscriptionError?.(subscriptionError);
    },
    [onSubscriptionError]
  );

  // Callers commonly pass unmemoized callbacks, which would otherwise make every handler
  // a new identity each render and tear the subscription effect down with them. Routing
  // them through a ref keeps the effect keyed on the subscription inputs alone.
  const handlers = {
    handleMessageCreated,
    handleMessageUpdated,
    handleMessageDeleted,
    handleMessagePinned,
    handleMessageUnpinned,
    handleReactionAdded,
    handleReactionRemoved,
    handleChatEnabled,
    handleChatDisabled,
    handleChatDeactivated,
    handleUserBanned,
    handleSubscriptionSuccess,
    handleSubscriptionError,
    onSubscriptionError,
  };

  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // ---------------------------------------------------------------------------
  // Channel Subscription Management
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!enabled || !chats.length || !isConnected || hasError) {
      return;
    }

    const subscriptions: {
      channelId: string;
      events: { name: string; callback: (data: unknown) => void }[];
    }[] = [];

    chats.forEach((chat) => {
      if (!chat.id) return;
      const chatMode = chat.mode as ChatMode;
      const channels = chat.channels ?? [];
      channels.forEach((channel) => {
        const channelIdentifier = channel?.name || channel?.id;
        if (!channelIdentifier) {
          return;
        }

        // Nothing is published to presence channels, so subscribing is pure member fan-out.
        if (isPresenceChannel(channel)) {
          return;
        }

        const events = [
          // Message events
          {
            name: 'message-created',
            callback: (data: unknown) => handlersRef.current.handleMessageCreated(data, chatMode),
          },
          {
            name: 'message-updated',
            callback: (data: unknown) => handlersRef.current.handleMessageUpdated(data, chatMode),
          },
          {
            name: 'message-deleted',
            callback: (data: unknown) => handlersRef.current.handleMessageDeleted(data, chatMode),
          },
          {
            name: 'message-pinned',
            callback: (data: unknown) => handlersRef.current.handleMessagePinned(data, chatMode),
          },
          {
            name: 'message-unpinned',
            callback: (data: unknown) => handlersRef.current.handleMessageUnpinned(data, chatMode),
          },
          // Reaction events
          {
            name: 'reaction-added',
            callback: (data: unknown) => handlersRef.current.handleReactionAdded(data, chatMode),
          },
          {
            name: 'reaction-removed',
            callback: (data: unknown) => handlersRef.current.handleReactionRemoved(data, chatMode),
          },
          // Chat state events
          {
            name: 'chat-enabled',
            callback: (data: unknown) => handlersRef.current.handleChatEnabled(data, chatMode),
          },
          {
            name: 'chat-disabled',
            callback: (data: unknown) => handlersRef.current.handleChatDisabled(data, chatMode),
          },
          {
            name: 'chat-deactivated',
            callback: (data: unknown) => handlersRef.current.handleChatDeactivated(data, chatMode),
          },
          // User events
          {
            name: 'user-banned',
            callback: (data: unknown) => handlersRef.current.handleUserBanned(data, chatMode),
          },
          // Subscription events
          {
            name: 'subscription_succeeded',
            callback: () => handlersRef.current.handleSubscriptionSuccess(),
          },
          {
            name: 'subscription_error',
            callback: (err: unknown) => handlersRef.current.handleSubscriptionError(err),
          },
        ];

        subscriptions.push({ channelId: channelIdentifier, events });

        try {
          subscribeToChannel(channelIdentifier, events);
        } catch (err) {
          logError('useLiveActivityChat: Subscription error for', channelIdentifier, ':', err);
          const subscriptionErr = err instanceof Error ? err : new Error(String(err));
          handlersRef.current.onSubscriptionError?.(subscriptionErr);
        }
      });
    });

    // Cleanup function
    return () => {
      subscriptions.forEach(({ channelId, events }) => {
        try {
          unsubscribeFromChannel(channelId, events);
        } catch (err) {
          logError('useLiveActivityChat: Unsubscription error for', channelId, ':', err);
        }
      });
      isSubscribedRef.current = false;

      // Clear all pending event timeouts
      expectedEventsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      expectedEventsRef.current.clear();
    };
  }, [enabled, chats, isConnected, hasError, subscribeToChannel, unsubscribeFromChannel]);

  return {
    isConnected,
    isSubscribed: isSubscribedRef.current,
    hasError,
    error,
    expectEvent,
    cancelExpectedEvent,
  };
};
